import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../../config";
import RoleDashboard from "./RoleDashboard";
import ScheduleGrid from "../../academics/ScheduleGrid";
import SectionRosterModal from "../../academics/SectionRosterModal";
import StudentDetailModal from "../../../components/StudentDetailModal";
import TeacherSectionsPanel from "./TeacherSectionsPanel";
import { parseSchedule } from "../../../components/SchedulePicker";
import s from "./TeacherDashboard.module.css";

/** "08:30" → 510 (minutes since midnight). null on bad input. */
function timeToMinutes(t) {
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
const DAY_KEYS_BY_INDEX = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Friendly 12-hour clock for "Up Next" — "08:00" → "8:00 AM". */
function formatTime(t) {
  const m = t?.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t || "";
  const h = Number(m[1]);
  const mn = m[2];
  const hh12 = ((h + 11) % 12) + 1;
  return `${hh12}:${mn} ${h < 12 ? "AM" : "PM"}`;
}

/* ── Data hook ──────────────────────────────────────────────────────── */
/**
 * Fetches everything the dashboard needs in one parallel batch. The three
 * sensitive endpoints (sections, students, enrollments) carry a
 * `?teacherStaffId=X` query so the backend filters server-side and never
 * returns rows from other teachers' sections. Auth is validated inside the
 * controllers by RequestAuth — a logged-in teacher querying their own id
 * passes; anything else returns 403.
 */
function useTeacherData(teacherStaffId) {
  const [state, setState] = useState({
    loading: true, error: "",
    offerings: [], sections: [], students: [], enrollments: [],
    subjects: [], semesters: [], schoolYears: [], rooms: [], gradeLevels: [],
  });

  useEffect(() => {
    if (!teacherStaffId) {
      setState({
        loading: false, error: "",
        offerings: [], sections: [], students: [], enrollments: [],
        subjects: [], semesters: [], schoolYears: [], rooms: [], gradeLevels: [],
      });
      return;
    }
    let cancelled = false;
    setState(p => ({ ...p, loading: true, error: "" }));
    const safe = (p) => p.then(r => r.ok ? r.json() : []).catch(() => []);
    Promise.all([
      safe(fetch(`${API_BASE}/api/class-offerings?teacherStaffId=${teacherStaffId}`)),
      safe(fetch(`${API_BASE}/api/sections?teacherStaffId=${teacherStaffId}`)),
      safe(fetch(`${API_BASE}/api/students?teacherStaffId=${teacherStaffId}`)),
      safe(fetch(`${API_BASE}/api/enrollments?teacherStaffId=${teacherStaffId}`)),
      safe(fetch(`${API_BASE}/api/subjects`)),
      safe(fetch(`${API_BASE}/api/semesters`)),
      safe(fetch(`${API_BASE}/api/school-years`)),
      safe(fetch(`${API_BASE}/api/rooms`)),
      safe(fetch(`${API_BASE}/api/grade-levels`)),
    ]).then(([offerings, sections, students, enrollments, subjects, semesters, schoolYears, rooms, gradeLevels]) => {
      if (cancelled) return;
      setState({
        loading: false, error: "",
        offerings: Array.isArray(offerings)   ? offerings   : [],
        sections:  Array.isArray(sections)    ? sections    : [],
        students:  Array.isArray(students)    ? students    : [],
        enrollments: Array.isArray(enrollments) ? enrollments : [],
        subjects:  Array.isArray(subjects)    ? subjects    : [],
        semesters: Array.isArray(semesters)   ? semesters   : [],
        schoolYears: Array.isArray(schoolYears) ? schoolYears : [],
        rooms:     Array.isArray(rooms)       ? rooms       : [],
        gradeLevels: Array.isArray(gradeLevels) ? gradeLevels : [],
      });
    }).catch(e => {
      if (!cancelled) setState(p => ({ ...p, loading: false, error: e?.message || "Failed to load" }));
    });
    return () => { cancelled = true; };
  }, [teacherStaffId]);

  return state;
}

/* ── Component ──────────────────────────────────────────────────────── */
export default function TeacherDashboard({ user }) {
  const navigate = useNavigate();
  const teacherStaffId = user?.linkedEntity?.id;
  const data = useTeacherData(teacherStaffId);

  // Modal state — section roster + student detail. Both read-only.
  const [rosterTarget, setRosterTarget] = useState(null);
  const [studentTarget, setStudentTarget] = useState(null);
  // Every class in the teacher's sections (including other teachers'
  // subjects) + employee names, for the Grade → Section panel.
  const [sectionOfferings, setSectionOfferings] = useState([]);
  const [employees,        setEmployees]        = useState([]);

  /* ── Lookups ──────────────────────────────────────────────────────── */
  const subjectsById   = useMemo(() => Object.fromEntries(data.subjects.map(x => [x.id, x])),   [data.subjects]);
  const sectionsById   = useMemo(() => Object.fromEntries(data.sections.map(x => [x.id, x])),   [data.sections]);
  const employeesById  = useMemo(() => Object.fromEntries(employees.map(x => [x.id, x])),       [employees]);
  const roomsById      = useMemo(() => Object.fromEntries(data.rooms.map(x => [x.id, x])),      [data.rooms]);
  const gradeLevelsById= useMemo(() => Object.fromEntries(data.gradeLevels.map(x => [x.id, x])),[data.gradeLevels]);

  /* ── Active term ──────────────────────────────────────────────────── */
  const activeSy = useMemo(
    () => data.schoolYears.find(y => y.active) || data.schoolYears[0] || null,
    [data.schoolYears]
  );
  const activeSem = useMemo(() => {
    if (!activeSy) return null;
    const inSy = data.semesters.filter(sm => String(sm.schoolYearId) === String(activeSy.id));
    return inSy.find(sm => sm.active) || inSy[0] || null;
  }, [data.semesters, activeSy]);

  /* ── Term-scoped offerings ────────────────────────────────────────── */
  // Filter offerings down to the current term so the schedule grid + cards
  // don't mix prior-semester ghost classes into "today". Falls open when
  // no active term exists yet (early-setup schools) — show everything.
  const termOfferings = useMemo(() => {
    if (!activeSem) return data.offerings;
    return data.offerings.filter(o => String(o.semesterId) === String(activeSem.id));
  }, [data.offerings, activeSem]);

  /* ── Today / Up Next ──────────────────────────────────────────────── */
  const todayKey = useMemo(() => DAY_KEYS_BY_INDEX[new Date().getDay()], []);
  const nowMins  = useMemo(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }, []);

  const todayPlaced = useMemo(() => {
    // Each entry: { offering, parsed, startMin, endMin }
    const out = [];
    for (const o of termOfferings) {
      const parsed = parseSchedule(o.schedule || "");
      if (!parsed.days.includes(todayKey)) continue;
      const sM = timeToMinutes(parsed.start);
      const eM = timeToMinutes(parsed.end);
      if (sM == null || eM == null) continue;
      out.push({ offering: o, parsed, startMin: sM, endMin: eM });
    }
    out.sort((a, b) => a.startMin - b.startMin);
    return out;
  }, [termOfferings, todayKey]);

  const nextUp = useMemo(() => {
    // Currently happening (within bounds) takes priority — that's the
    // "now-showing" class — otherwise the soonest upcoming.
    const ongoing = todayPlaced.find(t => nowMins >= t.startMin && nowMins < t.endMin);
    if (ongoing) return { ...ongoing, ongoing: true };
    const upcoming = todayPlaced.find(t => t.startMin >= nowMins);
    return upcoming ? { ...upcoming, ongoing: false } : null;
  }, [todayPlaced, nowMins]);

  /* ── All classes in the teacher's sections ───────────────────────── */
  // The teacher-scoped offerings only cover their own subjects; an adviser
  // also needs the rest of the section's subjects and who teaches them.
  const sectionIdsKey = data.sections.map(x => x.id).sort().join(",");
  useEffect(() => {
    if (!sectionIdsKey) { setSectionOfferings([]); return; }
    let cancelled = false;
    const safe = (u) => fetch(u).then(r => r.ok ? r.json() : []).catch(() => []);
    Promise.all([
      ...sectionIdsKey.split(",").map(id => safe(`${API_BASE}/api/class-offerings?sectionId=${id}`)),
      safe(`${API_BASE}/api/employees?all=true`),
    ]).then(results => {
      if (cancelled) return;
      const emps = results.pop();
      setEmployees(Array.isArray(emps) ? emps : []);
      setSectionOfferings(results.flatMap(x => (Array.isArray(x) ? x : [])));
    });
    return () => { cancelled = true; };
  }, [sectionIdsKey]);

  /** Section classes for the current term, merged with the teacher's own
   *  (deduped) so the panel never loses a class if one fetch fails. */
  const termSectionOfferings = useMemo(() => {
    const byId = new Map();
    for (const o of [...sectionOfferings, ...data.offerings]) {
      if (activeSem && String(o.semesterId) !== String(activeSem.id)) continue;
      byId.set(o.id, o);
    }
    return [...byId.values()];
  }, [sectionOfferings, data.offerings, activeSem]);

  /* ── Distinct rooms used in the active term ───────────────────────── */
  const roomChips = useMemo(() => {
    const seen = new Map();   // key → { label, meta }
    for (const o of termOfferings) {
      if (o.roomId && roomsById[o.roomId]) {
        const r = roomsById[o.roomId];
        const key = `id:${r.id}`;
        if (!seen.has(key)) {
          const metaParts = [];
          if (r.building) metaParts.push(r.building);
          if (r.roomType && r.roomType !== "STANDARD") metaParts.push(r.roomType);
          seen.set(key, { label: r.name || `#${r.id}`, meta: metaParts.join(" · ") });
        }
      } else if (o.room) {
        const key = `txt:${o.room}`;
        if (!seen.has(key)) seen.set(key, { label: o.room, meta: "" });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [termOfferings, roomsById]);

  /* ── Roster modal context resolver ────────────────────────────────── */
  function openRosterFor(sectionId) {
    const sec = sectionsById[sectionId];
    if (!sec) return;
    const grade = sec.gradeLevelId ? gradeLevelsById[sec.gradeLevelId] : null;
    setRosterTarget({
      section:        { id: sec.id, name: sec.name },
      gradeLabel:     grade?.name || null,
      facultyLabel:   null, // post Faculty→Department merge — not surfaced here
      adviserName:    null, // teacher likely IS the adviser; redundant
      schoolYearId:   activeSy?.id || null,
      semesterId:     activeSem?.id || null,
      schoolYearLabel: activeSy?.label || null,
      semesterLabel:   activeSem?.label || null,
    });
  }

  /* ── Render ───────────────────────────────────────────────────────── */
  const titleName = user?.linkedEntity?.displayName ? `, ${user.linkedEntity.displayName}` : "";
  const termPill = activeSy && activeSem
    ? `${activeSy.label} · ${activeSem.label} sem`
    : (activeSy?.label || "");

  return (
    <RoleDashboard
      eyebrow="Teacher"
      title={`Welcome${titleName}.`}
      sub={termPill ? `Your workspace for the current term — ${termPill}.` : "Your assigned classes, students, and weekly schedule."}
      user={user}
    >
      {/* ── Account-not-linked guard ────────────────────────────────── */}
      {!teacherStaffId && (
        <div className={s.empty}>
          Your account isn't linked to a Staff record yet. Ask an admin to set
          {" "}<code>linkedEntityType=STAFF</code> and <code>linkedEntityId</code> on your user.
        </div>
      )}

      {teacherStaffId && data.loading && <div className={s.empty}>Loading your dashboard…</div>}
      {teacherStaffId && data.error && <div className={s.error}>{data.error}</div>}

      {teacherStaffId && !data.loading && !data.error && (
        <>
          {/* ── Up Next hero ──────────────────────────────────────── */}
          {nextUp && (() => {
            const o    = nextUp.offering;
            const subj = subjectsById[o.subjectId];
            const sec  = sectionsById[o.sectionId];
            const grade= sec?.gradeLevelId ? gradeLevelsById[sec.gradeLevelId] : null;
            const room = o.roomId ? roomsById[o.roomId] : null;
            const roomLabel = room?.name || o.room || "";
            const restCount = todayPlaced.length - 1 - todayPlaced.findIndex(t => t.offering.id === o.id);
            return (
              <div className={s.upNext}>
                <div className={s.upNextEyebrow}>{nextUp.ongoing ? "Right now" : "Up next today"}</div>
                <h2 className={s.upNextTitle}>{subj?.title || subj?.code || "Class"}</h2>
                <div className={s.upNextMeta}>
                  <span><strong>{formatTime(nextUp.parsed.start)}</strong> – <strong>{formatTime(nextUp.parsed.end)}</strong></span>
                  {sec    && <span>{grade ? `${grade.name} · ` : ""}Section {sec.name}</span>}
                  {roomLabel && <span>Room {roomLabel}</span>}
                </div>
                {restCount > 0 && (
                  <div className={s.upNextRest}>
                    {restCount} more class{restCount === 1 ? "" : "es"} after this today.
                  </div>
                )}
              </div>
            );
          })()}
          {!nextUp && todayPlaced.length === 0 && termOfferings.length > 0 && (
            <div className={s.upNext}>
              <div className={s.upNextEyebrow}>Today</div>
              <h2 className={s.upNextTitle}>No classes scheduled today.</h2>
              <div className={s.upNextRest}>Take a breath — your week schedule is below.</div>
            </div>
          )}

          {/* ── Stats strip ───────────────────────────────────────── */}
          <div className={s.statStrip}>
            <div className={s.stat}>
              <div className={s.statLabel}>Sections</div>
              <div className={s.statValue}>{data.sections.length}</div>
              <div className={s.statSub}>That you teach or advise</div>
            </div>
            <div className={s.stat}>
              <div className={s.statLabel}>Students</div>
              <div className={s.statValue}>{data.students.length}</div>
              <div className={s.statSub}>Across all your sections</div>
            </div>
            <div className={s.stat}>
              <div className={s.statLabel}>Classes</div>
              <div className={s.statValue}>{termOfferings.length}</div>
              <div className={s.statSub}>This term</div>
            </div>
            <div className={s.stat}>
              <div className={s.statLabel}>Rooms</div>
              <div className={s.statValue}>{roomChips.length}</div>
              <div className={s.statSub}>You're scheduled in</div>
            </div>
          </div>

          {/* ── Schedule ──────────────────────────────────────────── */}
          <div className={s.section}>
            <div className={s.sectionHead}>
              <h3 className={s.sectionTitle}>My Schedule</h3>
              {termPill && <div className={s.sectionSub}>{termPill}</div>}
            </div>
            <ScheduleGrid
              offerings={termOfferings}
              subjects={data.subjects}
              sections={data.sections}
              employees={[]}
              rooms={data.rooms}
              gradeLevels={data.gradeLevels}
              colorBy="subject"
              primaryFor="section"
              onBlockClick={(o) => openRosterFor(o.sectionId)}
            />
          </div>

          {/* ── My Sections: Grade tabs → Section → Subjects | Students ── */}
          <div className={s.section}>
            <div className={s.sectionHead}>
              <h3 className={s.sectionTitle}>My Sections</h3>
              <div className={s.sectionSub}>Pick a grade, then a section</div>
            </div>
            <TeacherSectionsPanel
              teacherStaffId={teacherStaffId}
              sections={data.sections}
              gradeLevels={data.gradeLevels}
              students={data.students}
              enrollments={data.enrollments}
              offerings={termSectionOfferings}
              subjectsById={subjectsById}
              employeesById={employeesById}
              roomsById={roomsById}
              activeSy={activeSy}
              onOpenRoster={openRosterFor}
              onOpenStudent={setStudentTarget}
              onAttendance={(o) => navigate(`/teacher/attendance/${o.id}`)}
              onGrades={(o) => navigate(`/teacher/grades/${o.id}`)}
            />
          </div>

          {/* ── My Rooms ──────────────────────────────────────────── */}
          {roomChips.length > 0 && (
            <div className={s.section}>
              <div className={s.sectionHead}>
                <h3 className={s.sectionTitle}>My Rooms</h3>
                <div className={s.sectionSub}>{roomChips.length} distinct</div>
              </div>
              <div className={s.roomChips}>
                {roomChips.map((c, i) => (
                  <span key={i} className={s.roomChip}>
                    {c.label}
                    {c.meta && <span className={s.roomChipMeta}>· {c.meta}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modals (read-only) ─────────────────────────────────────── */}
      <SectionRosterModal
        open={!!rosterTarget}
        section={rosterTarget?.section}
        gradeLabel={rosterTarget?.gradeLabel}
        facultyLabel={rosterTarget?.facultyLabel}
        adviserName={rosterTarget?.adviserName}
        schoolYearId={rosterTarget?.schoolYearId}
        semesterId={rosterTarget?.semesterId}
        schoolYearLabel={rosterTarget?.schoolYearLabel}
        semesterLabel={rosterTarget?.semesterLabel}
        students={data.students}
        enrollments={data.enrollments}
        sections={data.sections}
        gradeLevels={data.gradeLevels}
        schoolYears={data.schoolYears}
        semesters={data.semesters}
        rooms={data.rooms}
        onClose={() => setRosterTarget(null)}
      />
      <StudentDetailModal
        open={!!studentTarget}
        student={studentTarget}
        sections={data.sections}
        gradeLevels={data.gradeLevels}
        schoolYears={data.schoolYears}
        semesters={data.semesters}
        rooms={data.rooms}
        enrollments={data.enrollments}
        onClose={() => setStudentTarget(null)}
      />
    </RoleDashboard>
  );
}
